/**
 * Strategy Lifecycle Mock
 *
 * Shows the three lifecycle phases of a platform strategy:
 * define() → init() → onBar() with descriptions and the
 * repeating loop on onBar.
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faGear,
  faWandMagicSparkles,
  faRepeat,
  faArrowDown,
} from '@fortawesome/free-solid-svg-icons';

interface Phase {
  fn: string;
  label: string;
  icon: typeof faGear;
  color: string;
  description: string;
  code: string;
  runs: string;
}

const phases: Phase[] = [
  {
    fn: 'define()',
    label: 'Parameters',
    icon: faGear,
    color: '#f59e0b',
    description: 'Declare adjustable parameters like period lengths and thresholds.',
    code: 'ctx.param("fastLen", 9, { min: 5, max: 50 });',
    runs: 'Once at load',
  },
  {
    fn: 'init()',
    label: 'Indicators',
    icon: faWandMagicSparkles,
    color: '#3b82f6',
    description: 'Create indicators. They update automatically each bar.',
    code: 'ctx.indicator("ema", "ema", { period: ctx.p.fastLen });',
    runs: 'Once at start',
  },
  {
    fn: 'onBar()',
    label: 'Logic',
    icon: faRepeat,
    color: '#22c55e',
    description: 'Run entry and exit checks. Called once per candle.',
    code: 'if (q.crossOver(ctx.ind.ema, ctx.ind.sma))\n  ctx.order.market("LONG");',
    runs: 'Every bar',
  },
];

export default function StrategyLifecycleMock() {
  return (
    <div className="platform-mock">
      <div className="platform-mock-header">
        <span className="dot" /> Strategy Lifecycle: define → init → onBar
      </div>
      <div className="platform-mock-body" style={{ padding: '1rem 1rem 0.75rem' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {phases.map((phase, i) => (
            <div key={phase.fn}>
              {/* Phase row */}
              <div style={{
                display: 'flex', gap: '0.75rem', alignItems: 'flex-start',
              }}>
                {/* Timeline dot */}
                <div style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  flexShrink: 0, width: 28,
                }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: '50%',
                    background: `${phase.color}15`,
                    border: `2px solid ${phase.color}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <FontAwesomeIcon icon={phase.icon}
                      style={{ fontSize: '0.6rem', color: phase.color }} />
                  </div>
                </div>

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    display: 'flex', alignItems: 'baseline', gap: '0.5rem',
                    marginBottom: '0.15rem',
                  }}>
                    <code style={{
                      fontSize: '0.75rem', fontWeight: 700, color: phase.color,
                      fontFamily: 'ui-monospace, monospace',
                    }}>{phase.fn}</code>
                    <span style={{
                      fontSize: '0.5625rem', color: '#6b7280',
                      textTransform: 'uppercase', letterSpacing: '0.04em',
                    }}>{phase.runs}</span>
                  </div>
                  <div style={{
                    fontSize: '0.6875rem', color: '#9ca3af', marginBottom: '0.35rem',
                    lineHeight: 1.4,
                  }}>
                    {phase.description}
                  </div>
                  <div style={{
                    fontSize: '0.5625rem', color: '#d1d5db',
                    fontFamily: 'ui-monospace, monospace',
                    padding: '0.3rem 0.5rem', background: 'rgba(255,255,255,0.03)',
                    borderRadius: 4, whiteSpace: 'pre', overflowX: 'auto',
                    lineHeight: 1.5,
                  }}>
                    {phase.code}
                  </div>
                </div>
              </div>

              {/* Arrow connector */}
              {i < phases.length - 1 && (
                <div style={{
                  display: 'flex', alignItems: 'center', paddingLeft: 8,
                  height: 28,
                }}>
                  <FontAwesomeIcon icon={faArrowDown}
                    style={{ fontSize: '0.55rem', color: '#4b5563' }} />
                </div>
              )}
            </div>
          ))}

          {/* Loop indicator for onBar */}
          <div style={{
            marginTop: '0.5rem', marginLeft: 36,
            padding: '0.35rem 0.6rem',
            background: 'rgba(34,197,94,0.06)',
            border: '1px solid rgba(34,197,94,0.15)',
            borderRadius: 6, fontSize: '0.625rem', color: '#9ca3af',
            display: 'flex', alignItems: 'center', gap: '0.35rem',
          }}>
            <FontAwesomeIcon icon={faRepeat}
              style={{ fontSize: '0.5rem', color: '#22c55e' }} />
            <span>
              <span style={{ color: '#22c55e', fontWeight: 600 }}>onBar()</span> repeats
              for every candle in the dataset or incoming live bar.
            </span>
          </div>
        </div>
      </div>
      <div className="platform-mock-caption">
        Every strategy follows this three-phase lifecycle. Parameters and indicators are set up once; your trading logic runs on each new bar.
      </div>
    </div>
  );
}
